import { Driver, PaymentProfile } from '@/store/user/user.model';
import { Currency } from '@/data/country.config';

type RentalLineItemType = 'DRIVE' | 'PROTECTION' | 'CAR2GOPACKAGEDURATION' | 'DISTANCE';
export interface RentalState {
  drivers: Driver[];
  monthlyInvoiceUrls: string[];
  companyPaymentProfiles: PaymentProfile[];
  performingState?: PerformingState;
}

export interface PerformingState {
  stateId: number;
  performingStateUuid: string;
  ownerUuid: string;
  collectable: boolean;
  performing: boolean;
  outstandingInvoices: boolean;
  validPayment: boolean;
  changeById: number;
  lastUpdated: Date;
  collectableLastChanged: Date;
}

export interface Rental {
  chargedAmount: number;
  creditsUsed: boolean;
  currency: string;
  endTime: {
    timestamp: string;
  };
  startAddress: string;
  uuid: string;
}

export interface RentalDetail {
  uuid?: string;
  duration: number;
  currency: Currency;
  start: {
    address: string;
    latitude?: number;
    longitude?: number;
    time: Date;
  };
  end: {
    address: string;
    latitude?: number;
    longitude?: number;
    time: Date;
  };
  buildSeries: string;
  numberPlate: string;
  paymentProfileName?: string;
  driverName: string;
  invoiceUrl?: string;
  city: string;
  price: {
    packagesUsed: boolean;
    creditsUsed: boolean;
    containsTaxItem: boolean;
    priceItems: {
      amount: number;
      type: RentalLineItemType;
      description: string;
      unit: string;
      count: number;
      uuid?: string;
    }[];
    paymentsUsed?: {
      paymentType: string;
      amount: number;
      profileType: string;
      name: string;
    }[];
    totalAmount: number;
  };
}

export interface InvoiceDownload {
  data: ArrayBuffer;
  filename: string;
}

export enum InvoiceDownloadType {
  pdf = 'pdf',
  csv = 'csv',
}

export interface InvoiceDownloadParams {
  id: number | string;
  refCompanyId: number;
  type: InvoiceDownloadType;
}

export interface RentalsParams {
  id?: number;
  size?: number;
  paymentProfileId: number;
  from: string | Date;
  to: string | Date;
  customerId?: number;
  oldestMonth?: string;
  withVlmData?: boolean;
  timezone?: string;
}

export interface RentalLineItem {
  type: RentalLineItemType;
  count: number;
  unit: string;
  gross_amount: number;
  description: string;
}

export interface SortedMonthlyRentalData {
  data: {
    [key: string]: {
      totalTrips: number;
      totalDistanceInKm: number;
      totalEmissionsInGrams: number;
      currencies: {
        [key: string]: number;
      };
    };
  };
  currencies: (Currency | string)[];
  distanceUnit: string;
}

export interface VehicleData {
  masterdata: {
    internalModelSeries: string;
    brand: string;
    vin: string;
    plate: string;
    location: string;
    lifecycleState: string;
    legalEntityId: number;
  };
  technicalData: {
    certification?: {
      co2Emission?: {
        urban?: number;
        highway?: number;
        combined?: number;
      };
    };
  };
  id: string;
  updatedAt: string;
}

export interface RentalObject {
  vin: string;
  customer_id: number;
  customer_uuid: string;
  number_plate: string;
  build_series: string;
  location_name: string;
  payment_profile_id: number;
  rental_start: {
    address: string;
    time: Date;
  };
  rental_end: {
    address: string;
    time: Date;
  };
  duration: number;
  distance: number;
  distance_unit: string;
  total_gross: number;
  currency: Currency;
  line_items: RentalLineItem[];
  expense_code?: string;
  expense_code_id?: string;
  expense_code_remark?: string;
}

export interface RentalObjectWithVLMData extends RentalObject {
  vehicleData?: VehicleData;
}

export interface Rentals {
  total?: {
    currency: Currency;
    amount: number;
  };
  rentals?: RentalObjectWithVLMData[];
}

export interface SpecialPaymentObject {
  currency: Currency;
  summary: {
    totalGross: number;
    totalNet: number;
    totalVat: number;
    refunds: number;
    specialPayments: number;
  };
  items: SpecialPaymentLineItem[];
}

export interface SpecialPaymentLineItem {
  type: string;
  description: string;
  created: Date;
  invoiceUrl: string;
  currency: Currency;
  amountGross: number;
  amountNet: number;
  amountVat: number;
  source: string;
}

export interface SpecialPayments {
  specialPaymentsByCurrency: SpecialPaymentObject[];
}

export type TotalPerCurrency = {
  [currency: string]: number;
};

export interface GetCompanyInvoicesParams {
  refCompanyId: number;
  paymentProfileIds: number[];
  dateFrom?: string | Date;
  dateTo?: string | Date;
  invoice_type?: 'CREDIT' | 'DEBIT' | 'PREINVOICE';
  page: number;
  size?: number;
}

export interface LoadRentalActionParams {
  month: number | Date;
  paymentProfileIds: number[];
}

export interface CompanyInvoicesResponse {
  total: number;
  pageable: Pageable;
  content: CompanyInvoice[];
}

export interface CompanyInvoice {
  invoiceNumber: string;
  refInvoiceNumber: string;
  invoiceDate: Date;
  legalEntityId: number;
  legalEntityTimeZone: string;
  customerUUID: string;
  customerId: number;
  paymentProfileId: number;
  invoiceType: string;
  amountGross: number;
  amountVat: number;
  currencyCode: string;
  customerType: string;
  paymentProfileType: string;
  paymentType: string;
  items: PaymentItem[];
  rentals: InvoiceRental[];
  invoicePeriod: InvoicePeriod;
  invoicePayment: InvoicePayment;
}

export interface InvoicePayment {
  moneyAmount: InvoiceTransactionAmount;
  creditsAmount: InvoiceTransactionAmount;
  taxRate: number;
}

export interface InvoiceTransactionAmount {
  net: number;
  gross: number;
  vat: number;
}

export interface InvoicePeriod {
  from: Date;
  to: Date;
}

export interface PaymentItem {
  itemUuid: string;
  refItemUuid: string;
  itemType: string;
  itemCategory: string;
  description: string;
  paymentKey: string;
  locationId: number;
  locationTimezone: string;
  dateOfService: Date;
  currencyCode: string;
  amountGross: number;
  amountNet: number;
  amountVat: number;
  taxPercentage: number;
  rentalUuid: string;
  driverId: number;
  rentalPackageUuid: string;
  typedQuantity: TypedQuantity;
}

export interface TypedQuantity {
  amount: number;
  unit: string;
}

export interface InvoiceRental {
  rentalUuid: string;
  numberPlate: string;
  buildSeries: string;
  driverPersonId: number;
  startAddress: string;
  endAddress: string;
  startCoordinate: number[];
  endCoordinate: number[];
  startTime: Date;
  endTime: Date;
  distance: number;
  cost: Cost;
}

export interface Cost {
  amountGross: number;
  amountNet: number;
}

export interface Pageable {
  page: number;
  size: number;
  sort: Sort;
}

export interface Sort {
  orders: Order[];
}

export interface Order {
  direction: string;
  property: string;
  ignoreCase: string;
  nullHandling: string;
}

export interface PaymentProfileOwner {
  defaultProfile: {
    active: boolean;
    activePayment: {
      changeContext: 'ANONYMOUS' | 'BACKOFFICE';
      changedByUser: number;
      creationDate: Date;
      deleted: boolean;
      discriminator: string;
      lastUpdated: Date;
      nameOfBank: string;
      nameOfAccountOwner: string;
      bankCountryCode: string;
      iban: string;
      bic: string;
      mandateAccepted: true;
      mandateDate: Date;
      ownerId: number;
      ownerUuid: string;
      paymentId: number;
      paymentUuid: string;
    };
    activeSubscription: boolean;
    address: {
      additionalStreet?: string;
      addressId: number;
      addressType: 'BUSINESS';
      addressUuid: string;
      certifiedEmail?: string;
      changed: boolean;
      changedBy: string;
      city: string;
      companyName?: string;
      countryIsoCode: string;
      creationDate: Date;
      discriminator?: string;
      firstName?: string;
      fiscalCode?: string;
      isDeleted: boolean;
      isHomeAddress: boolean;
      lastName?: string;
      lastUpdated: Date;
      legalEntityId: number;
      middleName?: string;
      ownerUuid: string;
      paymentProfileUuid?: string;
      recipientCode?: string;
      salutation?: string;
      state?: string;
      street?: string;
      title?: string;
      vatNumber?: string;
      zipCode: string;
    };
    billable: boolean;
    billableStatusChangeDate: Date;
    billableStatusChangedByBUser: number;
    changedByUser: number;
    changedByUserUUID: string;
    creationDate: Date;
    deleted: boolean;
    lastUpdated: Date;
    legalEntityId: number;
    ownerId: number;
    ownerUuid: string;
    paymentProfileUuid: string;
    paymentUuid: string;
    profileId: number;
    profileName: string;
    profileType: 'BUSINESS';
    serviceProfile: boolean;
  };
  legalEntityId: number;
  mandateUsageCheck: Date;
  mustSendMandateNotification: boolean;
  ownerAddressId: number;
  ownerId: number;
  ownerState: string;
  ownerType: 'COMPANY';
  ownerUuid: string;
  performingState: PerformingState;
  paymentProfiles: PaymentProfile[];
}
